/**
 * Connection manager for orchestrating multiple WebSocket connections
 *
 * Manages a registry of active connections and routes messages by endpoint ID.
 * Enforces the 3-connection limit.
 */

import { toast } from 'sonner';
import {
  createEndpointConnection,
  extractConnectionParams,
  validateEndpoint,
  type EndpointClient
} from './connectionFactory';
import { createScopedReporter } from './errorReporter';
import type { ErrorMessage, RollRequestMessage, RollCancelledMessage, PlayerListMessage, ConnectionAckMessage, TrainingStatusMessage } from './websocket';
import { WebSocketClient } from './websocket';
import { useActiveConnectionsStore } from '@/stores/activeConnectionsStore';
import { useSessionStore } from '@/stores/sessionStore';
import type { SavedEndpoint, ConnectionStatus } from '@/types/endpoint';

const SERVICE_TAG = '[CONNECTION_MANAGER]';
const reporter = createScopedReporter('WEBSOCKET');

class ConnectionManager {
  private connections: Map<string, EndpointClient> = new Map();

  /**
   * Connect to an endpoint
   */
  async connect(endpoint: SavedEndpoint): Promise<void> {
    const { canConnect, setStatus } = useActiveConnectionsStore.getState();

    // Check connection limit
    if (!canConnect()) {
      reporter.warning('Cannot connect: Maximum 3 connections allowed');
      throw new Error('Maximum connection limit reached (3)');
    }

    // Validate endpoint
    if (!validateEndpoint(endpoint)) {
      reporter.error('Invalid connection configuration', {
        context: { endpointId: endpoint.id }
      });
      throw new Error('Invalid endpoint configuration');
    }

    // Check if already connected
    if (this.connections.has(endpoint.id)) {
      console.warn(`${SERVICE_TAG} Already connected to endpoint`, { id: endpoint.id });
      return;
    }

    console.info(`${SERVICE_TAG} Connecting to endpoint`, {
      id: endpoint.id,
      name: endpoint.name
    });

    // Update status to connecting
    setStatus(endpoint.id, 'connecting');

    try {
      // Create endpoint client (WebSocket or REST-based)
      const client = createEndpointConnection(endpoint);

      // Configure event handlers
      client.configure({
        onStatusChange: (status: ConnectionStatus) => {
          const mappedStatus = status === 'connecting' ? 'connecting' :
            status === 'connected' ? 'connected' :
              'disconnected';
          setStatus(endpoint.id, mappedStatus);
          // Sync player identity to sessionStore so RollPage can submit results
          if (status === 'connected' && endpoint.type === 'player') {
            const creds = endpoint.credentials as { playerName?: string };
            if (creds.playerName) {
              useSessionStore.getState().setPlayer(creds.playerName, creds.playerName);
            }
          }
        },
        onError: (message: ErrorMessage) => {
          setStatus(endpoint.id, 'error', message.message);
          reporter.error('Connection error', {
            context: { endpointId: endpoint.id, error: message.message }
          });
        },
        onRollRequest: (message: RollRequestMessage) => {
          const { pendingRollRequest, setPendingRollRequest } = useSessionStore.getState();
          // Deduplicate: backend re-delivers pending rolls on reconnect; client deduplicates by requestId
          if (pendingRollRequest?.requestId === message.requestId) {
            console.info(`${SERVICE_TAG} Ignoring duplicate roll request`, { requestId: message.requestId });
            return;
          }
          setPendingRollRequest({
            requestId: message.requestId,
            playerId: message.playerId,
            dice: message.dice,
            context: message.context,
            flavor: message.flavor,
            createdAt: message.createdAt,
            proofMode: message.proofMode,
          });
          toast('Roll request ready — capture your dice!', { icon: '🎲' });
        },
        onRollCancelled: (message: RollCancelledMessage) => {
          // TICKET-289: backend only fans `rollCancelled` to platform-role connections,
          // so receipt here implies this client is acting as the requesting platform
          // (e.g. a standalone web-app session). Look up the player name and toast.
          const { players, pendingRollRequest, setPendingRollRequest } = useSessionStore.getState();
          const player = players.find((p) => p.id === message.playerId);
          const playerName = player?.name ?? 'Player';
          console.info(`${SERVICE_TAG} Roll cancelled by player`, {
            requestId: message.requestId,
            playerId: message.playerId,
            playerName
          });
          // Clear any matching pending request state on this client
          if (pendingRollRequest?.requestId === message.requestId) {
            setPendingRollRequest(null);
          }
          toast(`Roll cancelled by ${playerName}`, { icon: '🚫' });
        },
        onPlayerList: (message: PlayerListMessage) => {
          useSessionStore.getState().setPlayers(message.players);
        },
        onConnectionAck: (message: ConnectionAckMessage) => {
          useSessionStore.getState().setSessionId(message.sessionId);
        }
      });

      // Extract connection parameters
      const params = extractConnectionParams(endpoint);
      if (!params) {
        throw new Error('Failed to extract connection parameters');
      }

      // Connect (params type varies by client)
      await client.connect(params as any);

      // Register client
      this.connections.set(endpoint.id, client);

      console.info(`${SERVICE_TAG} Successfully connected to endpoint`, { id: endpoint.id });
    } catch (error) {
      setStatus(endpoint.id, 'error', error instanceof Error ? error.message : 'Connection failed');
      throw error;
    }
  }

  /**
   * Disconnect from an endpoint
   */
  disconnect(endpointId: string): void {
    const client = this.connections.get(endpointId);
    if (!client) {
      console.warn(`${SERVICE_TAG} No connection found for endpoint`, { endpointId });
      return;
    }

    console.info(`${SERVICE_TAG} Disconnecting from endpoint`, { endpointId });
    client.disconnect();
    this.connections.delete(endpointId);

    const { clearConnection } = useActiveConnectionsStore.getState();
    clearConnection(endpointId);
  }

  /**
   * Disconnect all endpoints
   */
  disconnectAll(): void {
    console.info(`${SERVICE_TAG} Disconnecting all connections`);
    this.connections.forEach((client) => {
      client.disconnect();
    });
    this.connections.clear();

    const { clearAll } = useActiveConnectionsStore.getState();
    clearAll();
  }

  /**
   * Get client for an endpoint
   */
  getClient(endpointId: string): EndpointClient | undefined {
    return this.connections.get(endpointId);
  }

  /**
   * Check if endpoint is connected
   */
  isConnected(endpointId: string): boolean {
    const client = this.connections.get(endpointId);
    return client?.isConnected() ?? false;
  }

  /**
   * Push an onTrainingStatus handler to all active WebSocket clients.
   *
   * Because WebSocketClient.configure() merges (not replaces) handlers, this
   * is safe to call at any time without disturbing existing handlers such as
   * onRollRequest, onPlayerList, or onConnectionAck.
   *
   * @param handler - Callback invoked when a trainingStatus message arrives
   */
  subscribeToTrainingStatus(handler: (message: TrainingStatusMessage) => void): void {
    console.info(`${SERVICE_TAG} Subscribing to training status across all WebSocket clients`);
    this.connections.forEach((client) => {
      if (client instanceof WebSocketClient) {
        client.configure({ onTrainingStatus: handler });
      }
    });
  }

  /**
   * Reconcile activeConnectionsStore against actual WebSocket health.
   *
   * When the phone sleeps, the OS kills WebSocket connections but the store
   * may still show them as 'connected' or 'connecting'. This method detects
   * dead connections and auto-reconnects them if possible, so the user
   * doesn't have to manually tap Connect after unlocking their phone.
   */
  reconcileConnections(): void {
    const store = useActiveConnectionsStore.getState();
    const storeConnections = store.connections;
    let reconnecting = 0;
    let cleaned = 0;

    storeConnections.forEach((state, endpointId) => {
      if (state.status !== 'connected' && state.status !== 'connecting') return;

      const client = this.connections.get(endpointId);
      const alive = client?.isConnected() ?? false;

      if (!alive) {
        console.info(`${SERVICE_TAG} Stale connection detected`, { endpointId, storeStatus: state.status });

        // Auto-reconnect if the client has stored connection params
        if (client instanceof WebSocketClient && client.hasConnectionParams()) {
          console.info(`${SERVICE_TAG} Auto-reconnecting on wake`, { endpointId });
          client.resetAndReconnect();
          reconnecting++;
        } else {
          // No client or no params — clean up
          if (client) {
            client.disconnect();
            this.connections.delete(endpointId);
          }
          store.clearConnection(endpointId);
          cleaned++;
        }
      }
    });

    if (reconnecting > 0 || cleaned > 0) {
      console.info(`${SERVICE_TAG} Reconciled: ${reconnecting} reconnecting, ${cleaned} cleaned`);
    }
  }
}

// Export singleton instance
export const connectionManager = new ConnectionManager();

// Reconcile stale connections when the page becomes visible again (phone wake)
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      connectionManager.reconcileConnections();
    }
  });
}
